/**
 * Geocode an address using the Google Maps JavaScript API (browser).
 * Returns the same shape as the Geocoding REST API so callers don't need to change.
 * Requests go to maps.googleapis.com, not through the app.
 */
import { loadGoogleMapsScript } from './loadGoogleMapsScript';

const API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';

const GEO_SESSION_KEY = 'ff_geocode_cache';
const geocodeMemCache = new Map();

// Hydrate from sessionStorage so re-searches / page reloads skip the API.
try {
  const stored = sessionStorage.getItem(GEO_SESSION_KEY);
  if (stored) {
    const entries = JSON.parse(stored);
    if (Array.isArray(entries)) {
      for (const [k, v] of entries) geocodeMemCache.set(k, v);
    }
  }
} catch (_) { /* ignore */ }

function cacheGeocode(key, value) {
  geocodeMemCache.set(key, value);
  try {
    sessionStorage.setItem(GEO_SESSION_KEY, JSON.stringify([...geocodeMemCache.entries()]));
  } catch (_) { /* ignore */ }
}

/**
 * Geocode a single address. Returns { status, results } in REST API shape, or throws.
 * @param {string} address
 * @returns {Promise<{ status: string, results: Array<{ geometry: { location: { lat: number, lng: number } }, address_components: Array }>}>}
 */
export function geocodeAddress(address) {
  const trimmed = (address || '').trim();
  if (!trimmed) return Promise.resolve({ status: 'ZERO_RESULTS', results: [] });

  const cacheKey = `fwd:${trimmed.toLowerCase()}`;
  const cached = geocodeMemCache.get(cacheKey);
  if (cached) return Promise.resolve(cached);

  return loadGoogleMapsScript(API_KEY).then(
    () =>
      new Promise((resolve, reject) => {
        if (!window.google?.maps?.Geocoder) {
          resolve({ status: 'ERROR', results: [] });
          return;
        }
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { address: trimmed, region: 'us' },
          (results, status) => {
            if (status !== window.google.maps.GeocoderStatus.OK) {
              resolve({
                status: status === window.google.maps.GeocoderStatus.ZERO_RESULTS ? 'ZERO_RESULTS' : 'ERROR',
                results: [],
              });
              return;
            }
            const restShape = {
              status: 'OK',
              results: (results || []).map((r) => ({
                geometry: {
                  location: {
                    lat: typeof r.geometry?.location?.lat === 'function' ? r.geometry.location.lat() : r.geometry?.location?.lat,
                    lng: typeof r.geometry?.location?.lng === 'function' ? r.geometry.location.lng() : r.geometry?.location?.lng,
                  },
                },
                address_components: (r.address_components || []).map((c) => ({
                  long_name: c.long_name || '',
                  short_name: c.short_name || '',
                  types: c.types || [],
                })),
              })),
            };
            cacheGeocode(cacheKey, restShape);
            resolve(restShape);
          }
        );
      })
  );
}

/**
 * Reverse-geocode a lat/lng to a formatted address. Uses Google Maps Geocoder in the browser.
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<string>} Formatted address or empty string on failure
 */
export function reverseGeocode(lat, lng) {
  if (typeof lat !== 'number' || typeof lng !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return Promise.resolve('');
  }

  const cacheKey = `rev:${lat.toFixed(5)},${lng.toFixed(5)}`;
  const cached = geocodeMemCache.get(cacheKey);
  if (cached) return Promise.resolve(cached);

  return loadGoogleMapsScript(API_KEY).then(
    () =>
      new Promise((resolve) => {
        if (!window.google?.maps?.Geocoder) {
          resolve('');
          return;
        }
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { location: { lat, lng }, region: 'us' },
          (results, status) => {
            if (status !== window.google.maps.GeocoderStatus.OK || !results?.length) {
              resolve('');
              return;
            }
            const addr = results[0].formatted_address || '';
            const result = typeof addr === 'string' ? addr : '';
            if (result) cacheGeocode(cacheKey, result);
            resolve(result);
          }
        );
      })
  );
}
